import { HeadlessButton, Icon, Text } from '@cellula/react';
import { primitiveColorTokens, tokens } from '@cellula/react-theme-patient';
import type { ReactNode, MouseEvent } from 'react';
import styled from 'styled-components';

const elements = {
	container: styled(HeadlessButton)`
		all: unset;
		display: flex;
		width: 100%;
		justify-content: space-between;
		border-radius: ${tokens.borderRadius.medium};
		border: 1px solid ${tokens.color.border.muted};
		box-shadow: ${tokens.elevation.skim};
		transition: box-shadow 150ms ease-in;
		color: ${tokens.color.content.default};
		background: ${tokens.color.bg.surface};
		overflow: hidden;
		position: relative;
		background: ${tokens.color.bg.surface};

		min-height: ${tokens.size.x8};

		&:hover {
			transition: box-shadow 50ms;
			box-shadow: ${tokens.elevation.lifted};
		}

		&:active {
			box-shadow: none;
		}

		&[aria-disabled='true'] {
			box-shadow: none;
			opacity: 0.7;
			cursor: not-allowed;

			&::after {
				content: '';
				position: absolute;
				inset: 0;
				background-color: ${primitiveColorTokens.neutral[100]};
				z-index: -1;
				opacity: 0.4;
			}
		}

		&:focus-visible {
			outline: 2px solid ${tokens.color.focus};
			outline-offset: 2px;
		}

		&[data-variant='chunky'] {
			min-height: 120px;
		}
	`,
	iconContainer: styled.span`
		display: flex;
		background-color: ${tokens.color.bg.surfaceBrand};
		color: ${tokens.color.content.mutedBrand};
		padding: ${tokens.spacing.x1_5};
		width: ${tokens.size.x7};
		flex-shrink: 0;
		flex-grow: 0;
		position: relative;

		&[data-align='center'] {
			align-items: center;
		}
	`,
	contentContainer: styled.span`
		display: flex;
		flex-grow: 1;
		padding: ${tokens.spacing.x2};
		align-items: center;

		&[data-align='top'] {
			align-items: flex-start;
		}
	`,
	content: styled.span`
		display: flex;
		flex-direction: column;
		gap: ${tokens.spacing.x1_5};
		align-items: flex-start;
		word-wrap: break-word;
		word-break: break-word;
	`,
	arrowContainer: styled.span`
		display: flex;
		align-items: flex-end;
		padding: ${tokens.spacing.x1_5} ${tokens.spacing.x1_5} ${tokens.spacing.x1_5} 0;
		color: ${tokens.color.content.muted};
		flex-shrink: 0;

		&[data-align='center'] {
			align-items: center;
		}
	`,
};

type Variants = 'slim' | 'chunky';

interface ActionCardRootProps {
	children: ReactNode;
	disabled?: boolean;
	isStatic?: boolean;
	icon?: JSX.Element;
	iconAlign?: 'top' | 'center';
	variant?: Variants;
	hideArrow?: boolean;
	arrowAlign?: 'bottom' | 'center';
	onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}
function ActionCardRoot({
	children,
	disabled,
	isStatic,
	icon,
	iconAlign = 'top',
	hideArrow = false,
	arrowAlign = 'center',
	variant = 'slim',
	onClick,
	...rest
}: ActionCardRootProps): JSX.Element {
	return (
		<elements.container
			as={isStatic ? 'span' : undefined}
			disabled={disabled}
			aria-disabled={isStatic && disabled ? 'true' : undefined}
			onClick={onClick}
			data-variant={variant}
			{...rest}
		>
			{icon ? <elements.iconContainer data-align={iconAlign}>{icon}</elements.iconContainer> : null}
			<elements.contentContainer data-align={variant === 'chunky' ? 'top' : 'center'}>{children}</elements.contentContainer>
			{!hideArrow ? (
				<elements.arrowContainer data-align={arrowAlign}>
					<Icon type='arrowRight' size='small' />
				</elements.arrowContainer>
			) : null}
		</elements.container>
	);
}

interface ActionCardContentProps {
	children: ReactNode;
}
function ActionCardContent({ children }: ActionCardContentProps) {
	return <elements.content>{children}</elements.content>;
}

interface ActionCardTitleProps {
	children: ReactNode;
}
function ActionCardTitle({ children }: ActionCardTitleProps) {
	return <Text variant='labelLargeSemiBold'>{children}</Text>;
}

interface ActionCardMetaProps {
	children: ReactNode;
}
function ActionCardMeta({ children }: ActionCardMetaProps) {
	return (
		<Text variant='labelRegular' color={tokens.color.content.muted}>
			{children}
		</Text>
	);
}

export const ActionCard = {
	Root: ActionCardRoot,
	Content: ActionCardContent,
	Title: ActionCardTitle,
	Meta: ActionCardMeta,
};
